import React, { useState, useRef, useEffect } from 'react';
import DynamicTextMenu from 'components/Dashboard/Influence/CrmEmails/TinyEditor/DynamicTextMenu';
import { Portal } from 'react-portal';

const SubjectElement = ({ value, onChange, disabled = false, autoFocus = false, mailMergeFields = [] }) => {
  const [text, setText] = useState(value || '');
  const [showDynamicMenu, setShowDynamicMenu] = useState(false);
  const [atSignPosition, setAtSignPosition] = useState(null);
  const [filterText, setFilterText] = useState('');
  const inputRef = useRef(null);
  const styledOutputRef = useRef(null);

  const handleChange = (e) => {
    const newText = e.target.value;
    setText(newText);
    onChange(e);
  };

  const handleKeyDown = (e) => {
    // Detect @ sign typing (@ or Shift+2)
    if (e.key === '@' || (e.keyCode === 50 && e.shiftKey)) {
      e.preventDefault();

      const cursorPos = inputRef.current.selectionStart;
      const beforeAt = text.substring(0, cursorPos);
      const afterAt = text.substring(cursorPos);

      // Insert @ and update text
      const newText = beforeAt + '@' + afterAt;
      setText(newText);
      onChange({ target: { value: newText } });

      // Position cursor right after the @ for the next keystrokes
      const newCursorPos = cursorPos + 1;
      setTimeout(() => {
        inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }, 0);

      // Open menu
      setAtSignPosition(newCursorPos);
      setShowDynamicMenu(true);
      setFilterText('');
    }
  };

  const handleInputKeyDown = (e) => {
    // Handle menu navigation when open
    if (showDynamicMenu) {
      if (e.key === ' ' || /\s/.test(e.key) || (filterText === '' && e.key === 'Backspace')) {
        setShowDynamicMenu(false);
        setFilterText('');
      }
    }
  };

  useEffect(() => {
    if (typeof atSignPosition !== 'number') return;

    const updateFilterText = () => {
      try {
        const cursorPos = inputRef.current.selectionStart;

        // Check if the @ character still exists at the expected position
        const atCharPos = atSignPosition - 1;
        if (atCharPos < 0 || atCharPos >= text.length || text[atCharPos] !== '@') {
          // @ was deleted, close menu and reset
          setShowDynamicMenu(false);
          setFilterText('');
          setAtSignPosition(null);
          return;
        }

        // If the cursor moved before the @ position, leave @-context and close the menu
        if (cursorPos < atSignPosition) {
          setShowDynamicMenu(false);
          setFilterText('');
          setAtSignPosition(null);
          return;
        }

        const textAfterAt = text.substring(atSignPosition, cursorPos);

        // Check if we're still in the same context (no spaces)
        if (/\s/.test(textAfterAt)) {
          setShowDynamicMenu(false);
          setFilterText('');
          return;
        }

        // Menu should be open if we have an @ with no whitespace after it
        setShowDynamicMenu(true);
        setFilterText(textAfterAt);
      } catch (err) {
        console.error('Error updating filter text:', err);
      }
    };

    updateFilterText();
  }, [text, atSignPosition]);

  useEffect(() => {
    // Synchronize scroll position when text changes
    if (styledOutputRef.current && inputRef.current) {
      // Use requestAnimationFrame to ensure DOM is updated before scrolling
      const frameId = requestAnimationFrame(() => {
        const styledOutput = styledOutputRef.current;
        const input = inputRef.current;
        if (!styledOutput || !input) return;

        // Scroll styled-output to the same position as input
        styledOutput.scrollLeft = input.scrollLeft;

        // Ensure the last character is visible by scrolling to show content
        if (styledOutput.scrollWidth > styledOutput.clientWidth) {
          // Add a small buffer to ensure character is fully visible
          styledOutput.scrollLeft = Math.min(
            input.scrollLeft + 20,
            styledOutput.scrollWidth - styledOutput.clientWidth
          );
        }
      });

      return () => {
        cancelAnimationFrame(frameId);
      };
    }
  }, [text]);

  const handleSelectField = (field) => {
    try {
      // For subject mode, need to remove the @ and filterText first, then add the field
      const beforeAt = text.substring(0, atSignPosition - 1); // -1 to exclude the @
      const afterFilterText = text.substring(atSignPosition + filterText.length);

      // Insert @field (@ is already in the text at atSignPosition-1)
      const newText = beforeAt + '@' + field + afterFilterText;
      setText(newText);

      const event = new Event('change', { bubbles: true });
      inputRef.current.value = newText;
      inputRef.current.dispatchEvent(event);
      onChange({ target: { value: newText } });

      // Close menu and reset
      setShowDynamicMenu(false);
      setFilterText('');
      setAtSignPosition(null);

      // Focus back on input and scroll to the end to show inserted field
      setTimeout(() => {
        inputRef.current?.focus();
        const newCursorPos = (beforeAt + '@' + field).length;
        inputRef.current.setSelectionRange(newCursorPos, newCursorPos);

        // Scroll to the end to show the newly inserted field
        const maxScroll = inputRef.current.scrollWidth - inputRef.current.clientWidth;
        inputRef.current.scrollLeft = maxScroll;
        if (styledOutputRef.current) {
          styledOutputRef.current.scrollLeft = maxScroll;
        }
      }, 0);
    } catch (err) {
      console.error('Error selecting field:', err);
    }
  };

  const handleContainerClick = () => {
    inputRef.current?.focus();
  };

  const handleInputScroll = (e) => {
    // Synchronize the scroll position of styled-output with input
    if (styledOutputRef.current) {
      const styledOutput = styledOutputRef.current;
      const input = e.target;

      styledOutput.scrollLeft = input.scrollLeft;

      // Ensure the last character is visible by scrolling to show content
      if (styledOutput.scrollWidth > styledOutput.clientWidth) {
        styledOutput.scrollLeft = Math.min(input.scrollLeft + 20, styledOutput.scrollWidth - styledOutput.clientWidth);
      }
    }
  };

  const handleBlur = () => {
    requestAnimationFrame(() => {
      if (inputRef.current) {
        inputRef.current.scrollLeft = 0;
      }
      if (styledOutputRef.current) {
        styledOutputRef.current.scrollLeft = 0;
      }
    });
  };

  const renderStyledText = () => {
    const parts = text.split(/(@[\w-]+)/g);
    const validFields = ['salutation', ...mailMergeFields];

    return parts.map((part, index) => {
      if (part.startsWith('@')) {
        const fieldName = part.substring(1);
        const isValidField = validFields.includes(fieldName);

        return (
          <span key={index} className={isValidField ? 'email-subject-mention' : ''}>
            {part}
          </span>
        );
      } else {
        return <span key={index}>{part}</span>;
      }
    });
  };

  return (
    <div
      className={`subject-element ${disabled ? 'd-block text-truncate' : 'position-relative'}`}
      onClick={disabled ? undefined : handleContainerClick}
    >
      {disabled ? (
        renderStyledText()
      ) : (
        <>
          <div ref={styledOutputRef} className='styled-output'>
            {renderStyledText()}
          </div>
          <input
            ref={inputRef}
            type='text'
            value={text}
            onChange={handleChange}
            onKeyDown={(e) => {
              handleKeyDown(e);
              handleInputKeyDown(e);
            }}
            onScroll={handleInputScroll}
            onBlur={handleBlur}
            placeholder='Subject'
            className='mail-merge-subject'
            spellCheck={false}
            autoComplete='off'
            autoFocus={autoFocus}
          />
          {showDynamicMenu && (
            <Portal node={document && document.querySelector('body')}>
              <DynamicTextMenu
                isOpen={showDynamicMenu}
                setOpenDynamicTextMenu={setShowDynamicMenu}
                mailMergeFields={mailMergeFields}
                bookmark={null}
                filterTextProp={filterText}
                onSelectForSubject={handleSelectField}
                isSubjectMode={true}
              />
            </Portal>
          )}
        </>
      )}
    </div>
  );
};

export default SubjectElement;
